import { authClient } from "./auth-client";

const MAX_401_RETRIES = 2;
const MAX_5XX_NETWORK_RETRIES = 2;
const RETRY_BACKOFF_MS = 1000;
const AUTH_WAIT_TIMEOUT = 10000;

let authReadyPromise: Promise<void> | null = null;
let authReadyResolve: (() => void) | null = null;

function getAuthReadyPromise(): Promise<void> {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      authReadyResolve = resolve;
    });

    checkSessionReady();
  }
  return authReadyPromise;
}

let signInPromise: Promise<void> | null = null;

async function checkSessionReady(): Promise<void> {
  try {
    const session = await authClient.getSession();
    if (session.data?.user) {
      if (authReadyResolve) {
        authReadyResolve();
        authReadyResolve = null;
      }
      return;
    }

    if (signInPromise) {
      await signInPromise;
    } else {
      signInPromise = authClient.signIn.anonymous()
        .then(() => {})
        .catch((err: unknown) => { console.error("Anonymous sign-in failed:", err); })
        .finally(() => { signInPromise = null; });
      await signInPromise;
    }
  } catch (err) {
    console.error("Error checking session:", err);
  }

  setTimeout(checkSessionReady, 500);
}

function resetAuthReady(): void {
  authReadyPromise = null;
  authReadyResolve = null;
}

export async function waitForAuth(): Promise<boolean> {
  const timeoutPromise = new Promise<boolean>((resolve) => {
    setTimeout(() => resolve(false), AUTH_WAIT_TIMEOUT);
  });

  const authPromise = getAuthReadyPromise().then(() => true);

  return Promise.race([authPromise, timeoutPromise]);
}

export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const authReady = await waitForAuth();

  if (!authReady) {
    console.warn("Auth timeout, proceeding anyway");
  }

  const fetchInit: RequestInit = {
    ...init,
    credentials: "include",
  };

  let lastResponse: Response | null = null;

  for (let retryAttempt = 0; retryAttempt <= MAX_5XX_NETWORK_RETRIES; retryAttempt++) {
    if (retryAttempt > 0) {
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
    }

    try {
      for (let attempt = 0; attempt <= MAX_401_RETRIES; attempt++) {
        const response = await fetch(input, fetchInit);
        lastResponse = response;

        if (response.status !== 401) {
          if (response.status >= 500 && response.status < 600 && retryAttempt < MAX_5XX_NETWORK_RETRIES) {
            break; // outer loop will retry
          }
          return response;
        }

        if (attempt < MAX_401_RETRIES) {
          resetAuthReady();
          const refreshed = await waitForAuth();
          if (!refreshed) {
            console.warn("Auth refresh timeout");
            break;
          }
          await new Promise((r) => setTimeout(r, 100));
        }
      }

      if (lastResponse && lastResponse.status >= 500 && lastResponse.status < 600 && retryAttempt < MAX_5XX_NETWORK_RETRIES) {
        continue;
      }
      return lastResponse!;
    } catch (err) {
      if (retryAttempt < MAX_5XX_NETWORK_RETRIES) {
        continue;
      }
      throw err;
    }
  }

  return lastResponse!;
}

